import type { CSSProperties, FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface EnrichedPillProps {
    children: ReactNode;
    href?: string;
    interactive?: boolean;
    style?: CSSProperties;
    workspaceMention?: boolean;
}

/** Shared visual primitive for rich entity references in mentions, activity, notifications, and comments. */
const EnrichedPill: FC<EnrichedPillProps> = ({
    children,
    href,
    interactive = true,
    style,
    workspaceMention = false,
}) => {
    const { token } = useAntDesignTheme();
    const sharedStyle: CSSProperties = {
        alignItems: 'center',
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusSM,
        boxSizing: 'border-box',
        color: token.colorText,
        cursor: interactive ? 'pointer' : undefined,
        display: 'inline-flex',
        fontSize: token.fontSizeSM,
        fontWeight: 500,
        gap: '0.25rem',
        height: token.controlHeightSM,
        lineHeight: '1.25rem',
        margin: '0 0.125rem',
        maxWidth: '14rem',
        minWidth: 0,
        overflow: 'hidden',
        padding: '0 0.375rem 0 0.125rem',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        ...style,
    };
    const dataAttributes = {
        'data-enriched-pill': 'true',
        ...(workspaceMention ? { 'data-workspace-mention-pill': 'true' } : {}),
    };

    if (interactive && href) {
        return (
            <Link {...dataAttributes} to={href} style={sharedStyle}>
                {children}
            </Link>
        );
    }

    return (
        <span {...dataAttributes} style={sharedStyle}>
            {children}
        </span>
    );
};

export default EnrichedPill;
